import { ChangeEvent, ChangeEventHandler, FC, useEffect, useRef } from 'react';
import { useField } from 'formik';
import ReactInputMask from 'react-input-mask';
import { FieldGroup, IFormProps } from '.';
import TextControlViewModel from '../Models/TextControlViewModel';
import { useFormControls } from 'src/Hooks';

interface ITextControlProps extends IFormProps {
	model: TextControlViewModel;
}

/**
 * Три варианта отображения по ControlKey:
 * - SimpleText (однострочное поле, маска не обязательна)
 * - MultiStringText (textarea, без маски)
 * - OnlyCaption (без инпута)
 */
const TextControl: FC<ITextControlProps> = ({
	model,
	readOnly,
	setFieldValue,
}) => {
	const [inputProps, , helperProps] = useField(model.FullPath);
	const inputRef = useRef<HTMLInputElement>(null);
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const { nextFocus } = useFormControls('#DF');
	const isCaptionOnly = model.ControlKey === 'OnlyCaption';
	/**
	 * Маска для react-input-mask:
	 * '000-000-000 00' -> '999-999-999 99'
	 */
	const inputMask = (model.Mask || '').replaceAll('0', '9');
	/**
	 * Маска для react-input-mask:
	 * '999-999-999 99' -> '___-___-___ __'
	 */
	const emptyMaskValue = inputMask.replace(/[9a*]/g, '_');
	const safeValue = (v: unknown): string => {
		if (typeof v === 'number' && Number.isNaN(v)) {
			return '';
		}
		if (typeof v === 'string') {
			return v;
		}
		if (v === null || v === undefined) {
			return String(model.DefaultValue ?? '');
		}
		return String(v);
	};

	useEffect(() => {
		helperProps.setValue(model.Value, true);
		// eslint-disable-next-line
	}, [model.Value]);

	useEffect(() => {
		if (textAreaRef && textAreaRef.current) {
			textAreaRef.current.style.height = '0px';
			const scrollHeight = textAreaRef.current.scrollHeight + 14;
			textAreaRef.current.style.height = scrollHeight + 'px';
		}
	}, [textAreaRef, inputProps.value]);

	const onChangeValue: ChangeEventHandler<
		HTMLInputElement | HTMLTextAreaElement
	> = (event) => {
		let newValue: string = event.target.value;

		// Компонент маски при фокусе подставляет пустой шаблон маски, при
		// потере фокуса сбрасывает его обратно в пустую строку
		if (
			model.Mask &&
			!inputProps.value &&
			(!newValue || newValue === emptyMaskValue)
		) {
			return;
		}

		if (!newValue) {
			setFieldValue(model.FullPath, '');
		}

		// Ограничение ввода, символов не попадающих под Pinput ( regexp )
		if (model.regexp) {
			const regexMatch = newValue.match(model.regexp);
			if (regexMatch) {
				const filtered = regexMatch.join('');
				if (filtered !== newValue) {
					setAndNextFocusValue(event, filtered, true);
					return;
				}
			} else {
				newValue = '';
				model.Value = newValue;
			}
		}

		let needNextFocus = false;
		// Проверочки по маске
		if (model.Mask || model.MaxLength) {
			const unMasked = newValue.replaceAll('_', '');
			const maxLength = model.Mask ? model.Mask.length : model.MaxLength;
			// Ежели поле ввода достигло масимума, то фокусируемся на следующем поле
			if (unMasked.length === maxLength) {
				needNextFocus = true;
			}
			// Если превысели максимальную длину, то просто сваливаем отседова
			else if (unMasked.length > maxLength) {
				return;
			}
		}

		setAndNextFocusValue(event, newValue, needNextFocus);
	};

	/** Устанавливает значение поля и переключает фокус на следующие поле формы */
	const setAndNextFocusValue = (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		newValue: string,
		isNextFocus: boolean = false,
	) => {
		model.setValue(newValue);
		setFieldValue(model.FullPath, newValue);
		if (isNextFocus) {
			nextFocus(event.target);
		}
	};

	const renderInput = () => {
		if (model.ControlKey === 'SimpleText') {
			return (
				<ReactInputMask
					type="text"
					inputRef={inputRef}
					readOnly={!model.ControlEnabling || readOnly}
					mask={inputMask}
					placeholder={model.Mask}
					maskChar="_"
					minLength={model.MinLength}
					{...inputProps}
					value={String(safeValue(inputProps.value))}
					onChange={onChangeValue}
					id={model.FullPath}
				/>
			);
		} else if (model.ControlKey === 'MultiStringText') {
			return (
				<textarea
					ref={textAreaRef}
					style={{ minHeight: '36px' }}
					readOnly={!model.ControlEnabling || readOnly}
					maxLength={model.MaxLength}
					minLength={model.MinLength}
					{...inputProps}
					value={String(safeValue(inputProps.value))}
					onChange={onChangeValue}
					id={model.FullPath}
				/>
			);
		} else if (isCaptionOnly) {
			return null;
		}
	};

	return (
		<FieldGroup
			isCaptionOnly={isCaptionOnly}
			hidden={!model.ControlVisibility}
			name={model.FullPath}
			title={model.Caption}
			width={model.Width ?? 12}
			readOnly={!model.ControlEnabling || readOnly}
			isInvalid={!model.IsValid}
			errorMsg={model.errorMessage}
			isRequired={model.IsMandatory}
			tooltip={model.Tooltip}
		>
			{renderInput()}
		</FieldGroup>
	);
};

export default TextControl;
